export const executiveKeys = ["tenants", "subscriptions", "registrations", "onboarding", "notifications"] as const;
export type ExecutiveKey = typeof executiveKeys[number];
export type ExecutiveItem = { id: string; label: string; reason: string; updatedAt: string };
export type ExecutiveData = {
  metrics: Array<{ key: string; count: number }>;
  attentionCount: number;
  items: ExecutiveItem[];
  hasMore: boolean;
  oldestOutstandingAt: string | null;
  lastProcessedAt: string | null;
  workerConfiguredEnabled: boolean | null;
};
export type ExecutiveSection = {
  key: ExecutiveKey; status: "available" | "unavailable"; generatedAt: string | null; data: ExecutiveData | null;
};
export type ExecutiveSnapshot = { itemLimit: number; sections: ExecutiveSection[] };
export type ExecutiveTarget =
  | { kind: "tenant" | "registration"; id: string }
  | { kind: "tenants" | "registrations" | "notificationAudit" | "operations" };

const reasons: Record<ExecutiveKey, string[]> = {
  tenants: ["Provisioning", "Active", "Suspended", "Failed", "Archived", "Other"],
  subscriptions: ["Trial", "Active", "PastDue", "Suspended", "Expired", "Cancelled", "Other"],
  registrations: ["Started", "WaitingVerification", "ReadyToProvision", "Provisioning", "Completed", "Blocked", "Terminal", "RetryableFailure", "Rejected", "Stuck", "OnboardingBlocked", "Other"],
  onboarding: ["NotStarted", "InProgress", "Completed", "Blocked", "Other"],
  notifications: ["Pending", "UnfinishedAttempts", "Exhausted", "Discarded", "CompletedProcessing", "Other"]
};
function object(value: unknown): value is Record<string, unknown> { return value !== null && typeof value === "object" && !Array.isArray(value); }
function count(value: unknown): value is number { return Number.isSafeInteger(value) && (value as number) >= 0; }
function date(value: unknown): value is string { return typeof value === "string" && value.length <= 40 && Number.isFinite(Date.parse(value)); }
function nullableDate(value: unknown) { return value === null || date(value); }
function validData(data: unknown, key: ExecutiveKey, limit: number): data is ExecutiveData {
  if (!object(data) || !Array.isArray(data.metrics) || !Array.isArray(data.items)
    || !count(data.attentionCount) || typeof data.hasMore !== "boolean"
    || !nullableDate(data.oldestOutstandingAt) || !nullableDate(data.lastProcessedAt)
    || !(data.workerConfiguredEnabled === null || typeof data.workerConfiguredEnabled === "boolean")) return false;
  const metricKeys = new Set<string>();
  let total = -1; let sum = 0;
  for (const metric of data.metrics) {
    if (!object(metric) || typeof metric.key !== "string" || !count(metric.count)
      || metricKeys.has(metric.key) || (metric.key !== "Total" && !reasons[key].includes(metric.key))) return false;
    metricKeys.add(metric.key);
    if (metric.key === "Total") total = metric.count; else sum += metric.count;
  }
  if (total !== sum || data.attentionCount > total) return false;
  const expectedItems = key === "notifications" ? 0 : Math.min(limit, data.attentionCount);
  if (data.items.length !== expectedItems || data.hasMore !== (key !== "notifications" && data.attentionCount > limit)) return false;
  const ids = new Set<string>();
  return data.items.every(item => {
    if (!object(item) || typeof item.id !== "string" || !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(item.id)
      || ids.has(item.id) || typeof item.label !== "string" || item.label.length > 200
      || typeof item.reason !== "string" || !reasons[key].includes(item.reason) || !date(item.updatedAt)) return false;
    ids.add(item.id); return true;
  });
}

export function validateExecutiveSnapshot(value: unknown): value is ExecutiveSnapshot {
  if (!object(value) || value.itemLimit !== 10 || !Array.isArray(value.sections) || value.sections.length !== executiveKeys.length) return false;
  const keys = new Set<string>();
  return value.sections.every(section => {
    if (!object(section) || !executiveKeys.includes(section.key as ExecutiveKey) || keys.has(section.key as string)) return false;
    keys.add(section.key as string);
    return section.status === "unavailable" ? section.data === null && section.generatedAt === null
      : section.status === "available" && date(section.generatedAt) && validData(section.data, section.key as ExecutiveKey, 10);
  });
}

export async function getPlatformExecutive(token: string, signal: AbortSignal): Promise<ExecutiveSnapshot> {
  const response = await fetch("/api/v1/platform-console/executive", {
    method: "GET", headers: { Authorization: `Bearer ${token}` }, credentials: "omit", cache: "no-store", signal
  });
  if (response.ok) {
    const value: unknown = await response.json();
    if (validateExecutiveSnapshot(value)) return value;
  }
  // Raw problem bodies, query details and diagnostics are deliberately discarded.
  throw Object.assign(new Error("Platform executive data unavailable."), { status: response.status });
}
